/** Marketplace web app entry point.
 *
 *  ⛔ THE CLIENT NO LONGER HOLDS THE CATALOGUE. It used to download BOTH
 *  `/catalog/*.json` documents on every hydration — 1.62 MB raw / ~200 KB
 *  brotli of recipes plus 1.28 MB / ~200 KB of packs — and search, filter, sort
 *  and page them in the browser, to render 48 cards. Every list view now asks
 *  `/catalog/search` for ONE page, and every detail view fetches ONE row.
 *
 *  That means this file, not the renderer, is where a view's data comes from:
 *  `loadRoute` is the single owner of "what does this route need", and every
 *  interaction that narrows or reorders a list (typing, a chip, a sort, a page)
 *  is a REFETCH, not a local re-render. `template.ts` holds no search code at
 *  all — see the ⛔ note there.
 *
 *  Routing: path-based via History API for SEO. The SSR worker serves a
 *  fully-rendered document; JS hydrates on top for interactivity — and must
 *  never blank that document while its own fetch is in flight (see
 *  `shouldKeepSsrDocument`).
 */

import {
  renderMarketplace,
  initialState,
  supabaseConfig,
  qualifyingFacetsFromCounts,
  BROWSE_PAGE_SIZE,
  type MarketplaceState,
  type RecipeListing,
  type PackListing,
  type FacetKind,
  type PublisherProfile,
  type PublisherStats,
  type View,
  type SortKey,
} from './template.js';
import {
  searchPacks,
  searchRecipes,
  type CatalogPage,
  type CatalogSearchQuery,
} from './catalog-search.js';
import {
  fetchPackDetail,
  fetchPackRecipeListings,
  fetchRecipeBundleInstallContext,
  fetchRecipeDetail,
  type ListingRead,
} from './catalog-detail.js';
import {
  LISTING_FEATURED_ORDER,
  LISTING_FILTER,
  LISTING_SELECT,
  type ListingCatalogue,
} from './catalog-query.js';

// ────────────────────────────────────────────────────────────────
// State
// ────────────────────────────────────────────────────────────────

let state: MarketplaceState = initialState();
let root: HTMLElement | null = null;

/** True when the worker served a fully-rendered document into `#root`. The SSR
 *  page IS the content — hydration only swaps in interactivity, so the client
 *  must not replace it with a loader while its fetch is in flight. */
let ssrDocumentPresent = false;

/** True after the first data render — from then on the SPA owns the DOM. */
let hydrated = false;

/** Pre-hydration render decision, extracted so the choice itself is testable:
 *  keep the worker's server-rendered document on screen instead of clobbering
 *  it. `loading` would replace a fully-rendered page with a spinner (the hero
 *  blanked until the fetch returned); a bootstrap `error` would replace working
 *  static content with an error card — the SSR page works without JS (links are
 *  real anchors), so keeping it is strictly better. Once `hydrated`, the SPA
 *  renders every state as usual (including the retry error card). */
export const shouldKeepSsrDocument = (
  s: Pick<MarketplaceState, 'loading' | 'error'>,
  ssrPresent: boolean,
  alreadyHydrated: boolean,
): boolean => ssrPresent && !alreadyHydrated && (s.loading || s.error !== null);

const setState = (partial: Partial<MarketplaceState>): void => {
  state = { ...state, ...partial };
  render();
};

const render = (): void => {
  if (!root) return;
  if (shouldKeepSsrDocument(state, ssrDocumentPresent, hydrated)) {
    if (state.error !== null) {
      console.error('[recued marketplace] bootstrap failed; keeping the server-rendered page:', state.error);
    }
    return;
  }
  // ⚠ CAPTURED BEFORE THE SWAP. `innerHTML` destroys the input the reader is
  // typing into, and `document.activeElement` becomes `<body>` the moment it
  // does — so asking afterwards always answers "not focused". Renders are now
  // driven by ASYNC fetches rather than by the keystroke itself, so one can
  // land mid-word; without this the caret is lost every time a search returns.
  const active = document.activeElement as HTMLInputElement | null;
  const typing = active?.dataset?.field === 'search';
  const caret = typing ? active?.selectionStart : null;

  root.innerHTML = renderMarketplace(state);
  attachHandlers();

  if (typing) {
    const input = root.querySelector<HTMLInputElement>('[data-field="search"]');
    if (input) {
      input.focus();
      const at = caret ?? input.value.length;
      input.selectionStart = input.selectionEnd = Math.min(at, input.value.length);
    }
  }
  if (!state.loading) hydrated = true;
};

// ────────────────────────────────────────────────────────────────
// Data fetching
// ────────────────────────────────────────────────────────────────

// Resolved once at bundle start: the SSR worker injects its env-scoped project
// as `window.RECUED_SUPABASE` in a script tag ABOVE this bundle, so staging
// pages read staging data. Absent injection (local dev shell) → baked default.
const SUPABASE = supabaseConfig();

const headers = {
  'apikey': SUPABASE.key,
  'Authorization': `Bearer ${SUPABASE.key}`,
};

/** The PostgREST reader the shared detail fetchers take — the browser half of
 *  the seam the SSR worker fills with its env. Same code runs on both sides,
 *  which is the point: the bundle-install resolution behind a pack's CTA is
 *  fail-closed and trust-relevant, and two copies of it would drift. */
const listingRead: ListingRead = (path) =>
  fetch(`${SUPABASE.url}/rest/v1/${path}`, { headers });

/** Whole-catalogue counts for the stats bar and the landing's "View all N".
 *
 *  These describe the MARKETPLACE, not the current query, so they are fetched
 *  once and reused — and topped up for free from any unfiltered search, whose
 *  `total` already IS the corpus count for that kind. */
let corpusTotals: { recipes?: number; packs?: number; publishers?: number } = {};

/** Publisher profiles: display names, bios, avatars and the certified flag.
 *  One row per publishing account — the smallest read on the page, and every
 *  view needs it, so it is fetched once. */
let publisherProfiles: PublisherProfile[] | null = null;
let publishersInFlight: Promise<PublisherProfile[]> | null = null;

const fetchPublishers = async (): Promise<PublisherProfile[]> => {
  const base = `${SUPABASE.url}/rest/v1/publishers`;
  const withCertified = await fetch(
    `${base}?select=id,display_name,bio,website,certified,created_at&order=id.asc`,
    { headers },
  );
  if (withCertified.ok) return withCertified.json();

  // Older deploys may not have the certified columns yet. Keep the public
  // marketplace browseable and let listing rows fall back to their own flag.
  const fallback = await fetch(
    `${base}?select=id,display_name,bio,website,created_at&order=id.asc`,
    { headers },
  );
  if (!fallback.ok) throw new Error(`Failed to load publishers: ${fallback.status}`);
  return fallback.json();
};

const ensurePublishers = async (): Promise<PublisherProfile[]> => {
  if (publisherProfiles !== null) return publisherProfiles;
  publishersInFlight ??= fetchPublishers()
    .catch(() => [] as PublisherProfile[])
    .then((rows) => {
      publisherProfiles = rows;
      corpusTotals.publishers = rows.length;
      return rows;
    });
  return publishersInFlight;
};

const applyPublisherProfiles = <T extends { publisher_id: string; publisher_certified?: boolean }>(
  rows: T[],
  publishers: PublisherProfile[],
): T[] => {
  const byId = new Map(publishers.map((p) => [p.id, p]));
  return rows.map((row) => {
    const profile = byId.get(row.publisher_id);
    if (!profile || profile.certified === undefined) return row;
    return { ...row, publisher_certified: profile.certified };
  });
};

/** The total off `Content-Range` — `0-2/1932` when rows came back, and the
 *  star-prefixed form on a 416, which still carries the true count. */
const CONTENT_RANGE_TOTAL = /\/(\d+)\s*$/;

/** One PostgREST window with its EXACT total, read off `Content-Range` — the
 *  same protocol the SSR worker's `sbWindow` uses. `count=exact` is what makes
 *  a three-row read able to say "1,932 recipes".
 *
 *  ⚠ A missing count THROWS rather than defaulting to 0. Every caller here is
 *  reporting a number to the reader — "View all N recipes", a publisher's total
 *  installs — and a silent 0 reads as an empty marketplace rather than as a
 *  failed read. */
const fetchWindow = async (
  query: string,
  offset: number,
  limit: number,
): Promise<{ rows: unknown[]; total: number }> => {
  const res = await fetch(`${SUPABASE.url}/rest/v1/${query}`, {
    headers: {
      ...headers,
      prefer: 'count=exact',
      range: `${offset}-${offset + limit - 1}`,
      'range-unit': 'items',
    },
  });
  // 206 Partial Content is the success case for a ranged read; 416 means the
  // window starts past the end, which is a legitimate empty answer that still
  // carries the true total.
  if (!res.ok && res.status !== 206 && res.status !== 416) {
    throw new Error(`Failed to load listings: ${res.status}`);
  }
  const header = res.headers.get('content-range') ?? '';
  const total = Number(CONTENT_RANGE_TOTAL.exec(header)?.[1]);
  if (!Number.isFinite(total)) {
    throw new Error(`Failed to load listings: no exact count in Content-Range ("${header}")`);
  }
  const json = (await res.json().catch(() => [])) as unknown;
  return { rows: Array.isArray(json) ? json : [], total };
};

/** Matches the SSR worker's `LANDING_FEATURED_POOL`: the landing shows three
 *  cards a side and the database can produce exactly those three in order, so
 *  the window is the size of the answer. */
const LANDING_FEATURED_CARDS = 3;

/** The landing page's three featured cards for one kind, plus that kind's
 *  corpus count. Deliberately NOT `/catalog/search` — see
 *  `LISTING_FEATURED_ORDER` for why the endpoint cannot express this order, and
 *  why both renderers of the landing page read the same one. */
const fetchFeatured = async (
  kind: ListingCatalogue,
): Promise<{ rows: unknown[]; total: number }> =>
  fetchWindow(
    `${kind}?${LISTING_FILTER}&order=${LISTING_FEATURED_ORDER(kind)}&select=${LISTING_SELECT[kind]}`,
    0,
    LANDING_FEATURED_CARDS,
  );

/** How many rows a publisher-stats walk reads per request. PostgREST caps a
 *  response at 1,000 rows regardless of what is asked for, so the aggregates
 *  page through rather than trusting one oversized read — a short read here
 *  would silently under-report a publisher's installs. */
const SCALAR_PAGE = 1000;

const fetchAllScalars = async (query: string): Promise<Record<string, unknown>[]> => {
  const out: Record<string, unknown>[] = [];
  for (let offset = 0; ; offset += SCALAR_PAGE) {
    const { rows, total } = await fetchWindow(query, offset, SCALAR_PAGE);
    out.push(...(rows as Record<string, unknown>[]));
    if (out.length >= total || rows.length === 0) return out;
  }
};

/** A publisher page's aggregates over that publisher's WHOLE catalogue.
 *
 *  Scalars only, and the walk is the point: an aggregate over one page is a
 *  wrong number rendered confidently. This mirrors the SSR worker's
 *  `fetchPublisherPage` — the grids beside it render one window, these numbers
 *  describe all of it. */
const fetchPublisherStats = async (handle: string): Promise<PublisherStats> => {
  const filter = `${LISTING_FILTER}&publisher_id=eq.${encodeURIComponent(handle)}`;
  const [recipes, packs] = await Promise.all([
    fetchAllScalars(`recipes?${filter}&order=recipe_id.asc&select=download_count,rating_count,created_at`),
    fetchAllScalars(`packs?${filter}&order=slug.asc&select=created_at`).catch(() => []),
  ]);
  const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
  const dates = [...recipes, ...packs]
    .map((r) => r.created_at)
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .sort((a, b) => b.localeCompare(a));
  return {
    recipes: recipes.length,
    packs: packs.length,
    installs: recipes.reduce((sum, r) => sum + num(r.download_count), 0),
    ratings: recipes.reduce((sum, r) => sum + num(r.rating_count), 0),
    latest: dates[0] ?? null,
  };
};

/** The query a list view sends. `sort` is always explicit — an empty one falls
 *  back to the spec's FIRST sorter (`downloads`), which is not this app's
 *  default (`newest`). */
const listQuery = (over: Partial<CatalogSearchQuery> = {}): CatalogSearchQuery => ({
  search: state.search,
  filters: {},
  sort: state.sort,
  page: state.page,
  perPage: BROWSE_PAGE_SIZE,
  ...over,
});

/** The recipe browse view's active facet selections, as the endpoint takes
 *  them. A `null` filter is simply absent — the endpoint treats an empty
 *  selection as a pass-through, and sending `f.platform=` would narrow to
 *  nothing. */
const recipeFilters = (): Record<string, string[]> => ({
  ...(state.platformFilter ? { platform: [state.platformFilter] } : {}),
  ...(state.tagFilter ? { tag: [state.tagFilter] } : {}),
  ...(state.publisherFilter ? { publisher: [state.publisherFilter] } : {}),
});

const packFilters = (): Record<string, string[]> => ({
  ...(state.packKindFilter ? { pack_kind: [state.packKindFilter] } : {}),
  ...(state.tagFilter ? { tag: [state.tagFilter] } : {}),
  ...(state.publisherFilter ? { publisher: [state.publisherFilter] } : {}),
});

/** Fill in whichever corpus counts are still unknown, from a one-row unfiltered
 *  search per missing kind. Cheap, cached, and usually already satisfied by an
 *  unfiltered browse that ran first. */
const ensureCorpusTotals = async (): Promise<void> => {
  const empty = { search: '', filters: {}, sort: 'newest', page: 1, perPage: 1 };
  const wanted: Promise<void>[] = [];
  if (corpusTotals.recipes === undefined) {
    wanted.push(searchRecipes(empty).then((p) => { corpusTotals.recipes = p.total; }).catch(() => {}));
  }
  if (corpusTotals.packs === undefined) {
    wanted.push(searchPacks(empty).then((p) => { corpusTotals.packs = p.total; }).catch(() => {}));
  }
  await Promise.all(wanted);
};

/** A search whose query narrows nothing has just measured the corpus. */
const noteCorpusTotal = (
  kind: 'recipes' | 'packs',
  query: CatalogSearchQuery,
  page: CatalogPage<unknown>,
): void => {
  if (query.search === '' && Object.keys(query.filters).length === 0) {
    corpusTotals[kind] = page.total;
  }
};

const totalsOf = (): MarketplaceState['totals'] => ({
  recipes: corpusTotals.recipes ?? 0,
  packs: corpusTotals.packs ?? 0,
  publishers: corpusTotals.publishers ?? 0,
});

// ────────────────────────────────────────────────────────────────
// Route loading
// ────────────────────────────────────────────────────────────────

/** Monotonic guard. Every load stamps itself; a response whose stamp is stale
 *  is DROPPED. Without it a slow first keystroke can land after a fast fourth
 *  one and put the wrong results on screen — the classic search race. */
let loadGeneration = 0;
const stale = (gen: number): boolean => gen !== loadGeneration;

/** The landing's search preview gets its OWN counter. Sharing `loadGeneration`
 *  meant a keystroke cancelled the in-flight load of the page it was typed on:
 *  the featured cards never arrived, `loading` never cleared, and the SSR
 *  document sat there permanently. The two races are independent — a newer
 *  preview supersedes an older preview, not the page around it. */
let previewGeneration = 0;
const stalePreview = (gen: number): boolean => gen !== previewGeneration;

const loadFailed = (e: unknown): void => {
  setState({ loading: false, error: e instanceof Error ? e.message : String(e) });
};

const loadRecipeList = async (gen: number): Promise<void> => {
  const query = listQuery({ filters: recipeFilters() });
  const [publishers, page] = await Promise.all([
    ensurePublishers(),
    searchRecipes(query),
  ]);
  noteCorpusTotal('recipes', query, page);
  await ensureCorpusTotals();
  if (stale(gen)) return;
  setState({
    loading: false,
    error: null,
    recipes: applyPublisherProfiles(page.rows, publishers),
    packs: [],
    publishers,
    totals: totalsOf(),
    matched: { recipes: page.total },
    facets: page.facets,
    searchPreview: undefined,
  });
};

const loadPackList = async (gen: number): Promise<void> => {
  const query = listQuery({ filters: packFilters() });
  const [publishers, page] = await Promise.all([
    ensurePublishers(),
    searchPacks(query),
  ]);
  noteCorpusTotal('packs', query, page);
  await ensureCorpusTotals();
  if (stale(gen)) return;
  setState({
    loading: false,
    error: null,
    recipes: [],
    packs: applyPublisherProfiles(page.rows, publishers),
    publishers,
    totals: totalsOf(),
    matched: { packs: page.total },
    facets: page.facets,
    searchPreview: undefined,
  });
};

/** A platform hub. `totals.recipes` here is the FACET's count, not the
 *  catalogue's — exactly what the SSR's `fetchFacetPage` sets, and what keeps
 *  the hero reading "102 recipes" instead of "102 of 2,624". */
const loadFacetHub = async (gen: number, facet: FacetKind, value: string): Promise<void> => {
  const [publishers, page] = await Promise.all([
    ensurePublishers(),
    searchRecipes(listQuery({ search: '', filters: { [facet]: [value] } })),
  ]);
  if (stale(gen)) return;
  setState({
    loading: false,
    error: null,
    recipes: applyPublisherProfiles(page.rows, publishers),
    packs: [],
    publishers,
    totals: { recipes: page.total, packs: 0, publishers: publishers.length },
    matched: undefined,
    // No filter bar on a hub — the page IS the filter — so no chips to feed.
    facets: undefined,
    searchPreview: undefined,
  });
};

/** The hub-of-hubs. Renders COUNTS, no rows, so it asks for one row and reads
 *  the facet block. */
const loadFacetIndex = async (gen: number, facet: FacetKind): Promise<void> => {
  const page = await searchRecipes({
    search: '', filters: {}, sort: 'newest', page: 1, perPage: 1,
  });
  noteCorpusTotal('recipes', { search: '', filters: {}, sort: 'newest', page: 1, perPage: 1 }, page);
  if (stale(gen)) return;
  setState({
    loading: false,
    error: null,
    recipes: [],
    packs: [],
    facetIndex: { kind: facet, values: qualifyingFacetsFromCounts(page.facets[facet] ?? []) },
    searchPreview: undefined,
  });
};

const loadPublisherPage = async (gen: number, handle: string): Promise<void> => {
  const filters = { publisher: [handle] };
  const [publishers, recipePage, packPage, stats] = await Promise.all([
    ensurePublishers(),
    searchRecipes(listQuery({ search: '', filters, sort: 'newest' })),
    // Packs are a PREVIEW on this page, not a paged list — one control cannot
    // honestly drive two lists of different lengths — so always page 1.
    searchPacks(listQuery({ search: '', filters, sort: 'newest', page: 1 })),
    fetchPublisherStats(handle).catch(() => undefined),
  ]);
  if (stale(gen)) return;
  setState({
    loading: false,
    error: null,
    recipes: applyPublisherProfiles(recipePage.rows, publishers),
    packs: applyPublisherProfiles(packPage.rows, publishers),
    publishers,
    // Absent stats ⇒ the renderer counts the rows in hand, which on a paged
    // view under-reports. Fall back to the page totals, which are exact.
    publisherStats: stats ?? {
      recipes: recipePage.total, packs: packPage.total, installs: 0, ratings: 0, latest: null,
    },
    totals: undefined,
    matched: undefined,
    facets: undefined,
    searchPreview: undefined,
  });
};

/** The landing page: three featured cards a side (in the order BOTH renderers
 *  produce), the two corpus counts, and the publisher strip. */
const loadLanding = async (gen: number): Promise<void> => {
  const [publishers, recipes, packs] = await Promise.all([
    ensurePublishers(),
    fetchFeatured('recipes'),
    fetchFeatured('packs').catch(() => ({ rows: [], total: 0 })),
  ]);
  corpusTotals.recipes = recipes.total;
  corpusTotals.packs = packs.total;
  if (stale(gen)) return;
  setState({
    loading: false,
    error: null,
    recipes: applyPublisherProfiles(recipes.rows as RecipeListing[], publishers),
    packs: applyPublisherProfiles(packs.rows as PackListing[], publishers),
    publishers,
    totals: totalsOf(),
    matched: undefined,
    facets: undefined,
  });
  void loadSearchPreview(++previewGeneration);
};

/** How many results the landing's type-ahead preview shows per kind. */
const SEARCH_PREVIEW_ROWS = 3;

/** The landing's live search preview. Left ABSENT rather than empty while in
 *  flight — an empty preview renders "Nothing matches that search yet", which
 *  would flash on every keystroke before the answer arrived. */
const loadSearchPreview = async (gen: number): Promise<void> => {
  if (state.search === '') {
    if (!stalePreview(gen)) setState({ searchPreview: undefined });
    return;
  }
  const query = {
    search: state.search, filters: {}, sort: state.sort, page: 1, perPage: SEARCH_PREVIEW_ROWS,
  };
  const [publishers, recipes, packs] = await Promise.all([
    ensurePublishers(),
    searchRecipes(query),
    searchPacks(query),
  ]);
  if (stalePreview(gen)) return;
  setState({
    searchPreview: {
      recipes: applyPublisherProfiles(recipes.rows, publishers),
      packs: applyPublisherProfiles(packs.rows, publishers),
    },
  });
};

/** A recipe detail. Three reads, started together: the row (with its body), the
 *  bundle-install context that decides whether a pack owns the CTA, and a small
 *  platform-matched set for "Works well with".
 *
 *  ⚠ `state.recipes` is the ONE row, mirroring what `renderRecipeDocument`
 *  passes server-side. Related candidates ride in `state.related` instead of
 *  being mixed in, so nothing downstream can mistake them for the catalogue. */
const loadRecipeDetail = async (gen: number, slug: string): Promise<void> => {
  const [publishers, row] = await Promise.all([
    ensurePublishers(),
    fetchRecipeDetail(listingRead, slug),
  ]);
  if (stale(gen)) return;
  if (row === null) {
    // The row was linked from a cached page but the table no longer serves it
    // — unpublished in between. Say so; a permanent spinner would not.
    setState({
      loading: false, error: null, recipes: [], packs: [], publishers,
      detailBodyError: 'This listing is no longer published.',
    });
    return;
  }
  const [stamped] = applyPublisherProfiles([row], publishers);
  setState({
    loading: false,
    error: null,
    recipes: stamped ? [stamped] : [row],
    packs: [],
    publishers,
    detailBodyError: null,
    related: undefined,
    recipeBundleInstallContext: undefined,
  });

  const [context, related] = await Promise.all([
    fetchRecipeBundleInstallContext(listingRead, row),
    fetchRelatedRecipes(row, publishers),
  ]);
  if (stale(gen)) return;
  setState({
    ...(context !== undefined ? { recipeBundleInstallContext: context } : {}),
    related,
  });
};

/** Candidates for "Works well with", narrowed to the recipe's first platform.
 *
 *  ⚠ NARROWER THAN IT WAS. The old scan ranked the WHOLE catalogue by platform
 *  match (+2) plus tag overlap, so a strong tag match on another platform could
 *  surface; this asks the endpoint for one platform and lets the existing
 *  scoring rank those. Deliberate: the ranking is not expressible as a search,
 *  and the alternative was downloading 1.6 MB to fill a four-card strip. A
 *  recipe with no platforms gets no strip, exactly as before. */
const RELATED_CANDIDATES = 24;

const fetchRelatedRecipes = async (
  row: RecipeListing,
  publishers: PublisherProfile[],
): Promise<RecipeListing[]> => {
  const platform = (row.platforms ?? [])[0];
  if (typeof platform !== 'string' || platform === '') return [];
  try {
    const page = await searchRecipes({
      search: '',
      filters: { platform: [platform] },
      sort: 'popular',
      page: 1,
      perPage: RELATED_CANDIDATES,
    });
    return applyPublisherProfiles(page.rows, publishers);
  } catch {
    // Informational strip — omit it rather than failing the page.
    return [];
  }
};

/** A pack detail. `state.recipes` carries the pack's pinned member listings,
 *  mirroring `renderPackDocument`'s `recipes` option — display enrichment for
 *  the "What this pack installs" grid, which degrades to slugs without it. */
const loadPackDetail = async (gen: number, slug: string): Promise<void> => {
  const [publishers, row] = await Promise.all([
    ensurePublishers(),
    fetchPackDetail(listingRead, slug),
  ]);
  if (stale(gen)) return;
  if (row === null) {
    setState({
      loading: false, error: null, recipes: [], packs: [], publishers,
      detailBodyError: 'This listing is no longer published.',
    });
    return;
  }
  const [stamped] = applyPublisherProfiles([row], publishers);
  setState({
    loading: false,
    error: null,
    recipes: [],
    packs: stamped ? [stamped] : [row],
    publishers,
    detailBodyError: null,
    related: undefined,
  });

  const members = await fetchPackRecipeListings(listingRead, row);
  if (stale(gen) || members === undefined) return;
  setState({ recipes: applyPublisherProfiles(members, publishers) });
};

/** The single owner of "what does this route need". Every navigation and every
 *  narrowing interaction goes through here. */
const loadRoute = (): void => {
  const gen = ++loadGeneration;
  const run = (): Promise<void> => {
    switch (state.view) {
      case 'recipes': return loadRecipeList(gen);
      case 'packs': return loadPackList(gen);
      case 'facet':
        return state.facet
          ? loadFacetHub(gen, state.facet.kind, state.facet.value)
          : loadRecipeList(gen);
      case 'facet-index':
        return loadFacetIndex(gen, state.facetIndex?.kind ?? 'platform');
      case 'publisher':
        return state.detailSlug ? loadPublisherPage(gen, state.detailSlug) : loadLanding(gen);
      case 'recipe-detail':
        return state.detailSlug ? loadRecipeDetail(gen, state.detailSlug) : loadLanding(gen);
      case 'pack-detail':
        return state.detailSlug ? loadPackDetail(gen, state.detailSlug) : loadLanding(gen);
      case 'landing': return loadLanding(gen);
    }
  };
  run().catch((e) => {
    if (!stale(gen)) loadFailed(e);
  });
};

// ────────────────────────────────────────────────────────────────
// Routing
// ────────────────────────────────────────────────────────────────

export interface MarketplaceRoute {
  view: View;
  detailSlug: string | null;
  /** Set on the two facet routes. `value` is absent on the hub-of-hubs, which
   *  is about the facet itself rather than one of its values. */
  facet?: { kind: FacetKind; value?: string };
}

/** Parse the current pathname into a view + optional detail slug.
 *  Supports both /marketplace/recipe/{id} (subfolder deploy) and /recipe/{id} (root deploy). */
export const parseMarketplacePath = (pathname: string): MarketplaceRoute => {
  // Strip /marketplace prefix if present, then leading slash
  let path = pathname;
  if (path.startsWith('/marketplace')) path = path.slice('/marketplace'.length);
  path = path.replace(/^\/+|\/+$/g, '');

  // ⚠ BEFORE the detail branch below, which matches anything under `recipes/`.
  //
  // These are worker-rendered pages the SPA then hydrates on top of, so getting
  // them wrong is not a missing feature — it is the server's finished page being
  // REPLACED. Read as details, `/recipes/platform/hubspot` became a lookup for a
  // recipe named "platform/hubspot", and every one of the twenty live hub pages
  // rewrote itself to "That recipe is not in the marketplace yet." the moment
  // the catalog fetch returned.
  if (path === 'recipes/platform') {
    return { view: 'facet-index', detailSlug: null, facet: { kind: 'platform' } };
  }
  const hub = /^recipes\/platform\/([^/]+)$/.exec(path);
  if (hub) {
    // Lower-cased to match the worker's route normalisation, so one facet is
    // never two client states.
    return {
      view: 'facet',
      detailSlug: null,
      facet: { kind: 'platform', value: decodeURIComponent(hub[1]).toLowerCase() },
    };
  }

  if (path.startsWith('recipes/') && path.length > 8) {
    return { view: 'recipe-detail', detailSlug: decodeURIComponent(path.slice(8)) };
  }
  if (path.startsWith('packs/') && path.length > 6) {
    return { view: 'pack-detail', detailSlug: decodeURIComponent(path.slice(6)) };
  }
  if (path === 'packs') {
    return { view: 'packs', detailSlug: null };
  }
  if (path === 'recipes') {
    return { view: 'recipes', detailSlug: null };
  }
  if (path.startsWith('publishers/') && path.length > 11) {
    return { view: 'publisher', detailSlug: decodeURIComponent(path.slice(11)) };
  }
  return { view: 'landing', detailSlug: null };
};

/** True iff the path is a marketplace SPA view the client renders itself
 *  (landing home · recipe/pack lists + details · publisher pages). The folded
 *  apex routes (/bridge, /bridge-terms, /pricing, /docs, /get-started, /publish, /privacy,
 *  /terms, /billing, /support) are worker-served documents, NOT SPA views — clicks on
 *  them must fall through to a full browser navigation, or `parseMarketplacePath`
 *  would silently route them to the landing view. */
export const isMarketplaceRoute = (pathname: string): boolean => {
  let path = pathname.split('#')[0].split('?')[0];
  if (path.startsWith('/marketplace')) path = path.slice('/marketplace'.length);
  path = path.replace(/^\/+|\/+$/g, '');
  if (path === '' || path === 'recipes' || path === 'packs') return true;
  // SINGLE-segment only — mirrors what the Worker actually serves
  // (parseDetailPath / parseIndexPath match one segment). A nested path like
  // /recipes/a/b is NOT a marketplace view; let it full-load to the Worker's
  // shared-shell 404 instead of soft-routing it into a dead SPA detail view.
  return /^(recipes|packs|publishers)\/[^/]+$/.test(path);
};

const parseRoute = (): MarketplaceRoute =>
  parseMarketplacePath(window.location.pathname);

/** The facet half of a route, as state.
 *
 *  A hub page's rows arrive from the server ALREADY narrowed by the database,
 *  and the hydrated SPA now asks the search endpoint for the same narrowing —
 *  `platformFilter` is what carries the value into that query.
 *
 *  The hub-of-hubs needs no values here: `facetIndex.values` absent means "count
 *  them from the rows in hand", and the SPA fills it from the endpoint's facet
 *  counts as soon as its load returns. */
export const facetStateOf = (route: MarketplaceRoute): Pick<
  MarketplaceState, 'facet' | 'facetIndex' | 'platformFilter'
> => {
  if (route.view === 'facet-index' && route.facet) {
    return { facet: undefined, facetIndex: { kind: route.facet.kind }, platformFilter: null };
  }
  if (route.view === 'facet' && route.facet?.value) {
    const { kind, value } = route.facet;
    return { facet: { kind, value }, facetIndex: undefined, platformFilter: value };
  }
  return { facet: undefined, facetIndex: undefined, platformFilter: null };
};

/** `?page=N`, matching the SSR reader. The SPA must land on the SAME page the
 *  server rendered, or hydration silently swaps the reader's page 7 for page 1. */
const pageFromLocation = (): number => {
  const raw = Number(new URLSearchParams(window.location.search).get('page'));
  return Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;
};

/** `?search=` seeds the search box. A platform too small for its own hub page
 *  301s here, so this is what makes that redirect land on the thing the reader
 *  asked for instead of the unfiltered catalogue.
 *
 *  Split from `window.location` so the decision itself is testable — the same
 *  reason `shouldKeepSsrDocument` is. Bounded, because the value arrives from a
 *  URL anyone can type. */
export const searchFromQuery = (query: string): string =>
  new URLSearchParams(query).get('search')?.slice(0, 100) ?? '';

const searchFromLocation = (): string => searchFromQuery(window.location.search);

/** Navigate via History API instead of hash. */
const navigate = (href: string): void => {
  window.history.pushState(null, '', href);
  handleRoute();
};

const handleRoute = (): void => {
  const route = parseRoute();
  const { view, detailSlug } = route;
  state = {
    ...state,
    view,
    detailSlug,
    page: pageFromLocation(),
    search: searchFromLocation(),
    ...facetStateOf(route),
    tagFilter: null,
    publisherFilter: null,
    packKindFilter: null,
    kindFilter: null,
    // A new route's rows have their own outcome — carrying the last one's over
    // would show one view's content under another view's heading.
    detailBodyError: null,
    related: undefined,
    searchPreview: undefined,
    recipeBundleInstallContext: undefined,
  };
  render();
  loadRoute();
};

// ────────────────────────────────────────────────────────────────
// Event handlers
// ────────────────────────────────────────────────────────────────

/** Attached ONCE (delegated on the persistent `#root`, so it survives
 *  `innerHTML` swaps). Re-attaching per render stacked one listener per
 *  render — every click then pushed N identical history entries. Attached
 *  lazily from the first real render, so pre-hydration clicks on the SSR
 *  document stay full browser navigations to worker-rendered pages. */
let navDelegationAttached = false;
const ensureNavDelegation = (): void => {
  if (navDelegationAttached || !root) return;
  navDelegationAttached = true;

  // Intercept internal link clicks → History API navigation
  root.addEventListener('click', (ev) => {
    const anchor = (ev.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
    if (!anchor) return;
    const href = anchor.getAttribute('href') ?? '';
    // Soft-navigate ONLY the SPA's own views. Internal paths start with `/`
    // (skip external https://, mailto:, protocol-relative //). A folded apex
    // route (/pricing, /docs, /get-started, /publish, /privacy, …) is worker-
    // served, so let the browser do a full navigation rather than client-route
    // it to the landing fallback.
    if (href.startsWith('/') && !href.startsWith('//') && isMarketplaceRoute(href)) {
      ev.preventDefault();
      navigate(href);
    }
  });
};

/** Typing is a REFETCH now, not a re-render, so it is debounced — one request
 *  per pause, not one per keystroke. The monotonic `loadGeneration` guard is
 *  what makes an overtaking response safe; this is what keeps the count of them
 *  reasonable. */
const SEARCH_DEBOUNCE_MS = 220;
let searchTimer: ReturnType<typeof setTimeout> | undefined;

const onSearchInput = (value: string): void => {
  // Typing narrows the set — back to page 1, same as the filter buttons. The
  // state moves immediately so the input keeps its own value; only the fetch
  // waits.
  state = { ...state, search: value, page: 1 };
  if (searchTimer !== undefined) clearTimeout(searchTimer);
  searchTimer = setTimeout(() => {
    if (state.view === 'landing') {
      loadSearchPreview(++previewGeneration).catch(() => {
        // A failed preview leaves the previous one on screen; the featured
        // sections below it are unaffected.
      });
      return;
    }
    loadRoute();
  }, SEARCH_DEBOUNCE_MS);
};

/** Narrow or reorder, then refetch. Page 1 on every one of them: without it a
 *  reader on page 7 who filters lands on page 7 of a smaller set. */
const narrow = (partial: Partial<MarketplaceState>): void => {
  state = { ...state, ...partial, page: 1 };
  render();
  loadRoute();
};

const attachHandlers = (): void => {
  if (!root) return;
  ensureNavDelegation();

  // Search input
  const searchInput = root.querySelector<HTMLInputElement>('[data-field="search"]');
  if (searchInput) {
    searchInput.addEventListener('input', () => {
      onSearchInput(searchInput.value);
    });
  }

  // Filter + sort buttons
  const actionButtons = root.querySelectorAll<HTMLButtonElement>('[data-action]');
  for (const btn of actionButtons) {
    btn.addEventListener('click', (ev) => {
      ev.preventDefault();
      const action = btn.dataset.action;
      const value = btn.dataset.value ?? '';
      switch (action) {
        case 'filter-platform':
          narrow({ platformFilter: value || null });
          break;
        case 'filter-tag':
          narrow({ tagFilter: value || null });
          break;
        case 'filter-publisher':
          narrow({ publisherFilter: value || null });
          break;
        case 'filter-pack-kind':
          narrow({ packKindFilter: value || null });
          break;
        case 'sort':
          // Fallback matches initialState — 'popular' here was a stale default
          // after the catalogue moved to created_at DESC.
          narrow({ sort: (value as SortKey) || 'newest' });
          break;
        case 'clear-filters':
          narrow({
            platformFilter: null,
            tagFilter: null,
            publisherFilter: null,
            packKindFilter: null,
            kindFilter: null,
            search: '',
          });
          break;
        case 'copy': {
          // Restore the button's OWN label (Copy ID / Copy slug / Copy
          // publisher URL), not a generic 'Copy' that clobbers it (MP-6).
          // Stamp the label ONCE so a rapid second click can't capture the
          // transient 'Copied!' and leave the button stuck on it.
          if (btn.dataset.copyLabel === undefined) btn.dataset.copyLabel = btn.textContent ?? '';
          const original = btn.dataset.copyLabel;
          navigator.clipboard.writeText(value).then(() => {
            btn.textContent = 'Copied!';
            setTimeout(() => { btn.textContent = original; }, 1500);
          });
          break;
        }
        case 'retry':
          setState({ loading: true, error: null });
          loadRoute();
          break;
        case 'retry-detail':
          setState({ detailBodyError: null });
          loadRoute();
          break;
      }
    });
  }

};

// ────────────────────────────────────────────────────────────────
// Entry
// ────────────────────────────────────────────────────────────────

const init = (): void => {
  root = document.getElementById('root');
  if (!root) {
    console.error('[recued marketplace] #root not found');
    return;
  }

  // The worker serves every apex HTML page fully rendered into #root; an empty
  // root only occurs on the static dev shell (public/index.html).
  ssrDocumentPresent = root.childElementCount > 0;

  window.addEventListener('popstate', handleRoute);

  // Set initial view from URL path. The facet state rides along: landing
  // DIRECTLY on a hub is the case that matters, since there is no earlier
  // `handleRoute` to have set it.
  const route = parseRoute();
  state = {
    ...state,
    view: route.view,
    detailSlug: route.detailSlug,
    page: pageFromLocation(),
    search: searchFromLocation(),
    ...facetStateOf(route),
  };

  // No-op while the SSR document stands (see shouldKeepSsrDocument); renders
  // the loader only on the empty dev shell.
  render();
  loadRoute();
};

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
